<template>
  <div class="speed-chart-container">
    <div class="speed-line-description">
      <div class="box global">
        <img
          :alt="$t('home-speed-cloud-average')"
          :src="globalSpeedLineSVG"
          height="18"
          width="16"
        >
        <span>{{ $t('home-speed-cloud-average') }}</span>
      </div>

      <div class="box person">
        <img
          ref="speedImageElementRef"
          :alt="$t('home-speed-line-person')"
          :src="personSpeedLineSVG"
          height="18"
          width="16"
        >
        <span>{{ $t('home-speed-line-person') }}</span>
      </div>
    </div>

    <div class="speed-canvas-container">
      <div :class="{ 'actual-size': true, 'actual-size-animate': svgVisible }">
        <svg
          :class="{ 'speed-svg': true, 'speed-svg-animate': svgVisible }"
          :height="svgSizeData.height"
          :width="svgSizeData.width + svgBuffer"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g>
            <path
              ref="bezierPathRef"
              :d="pathData.bezierPath"
              class="speed-svg-line"
              fill="none"
              stroke="rgb(48,110,225)"
              style="stroke-width: 2px;"
            />
            <clipPath id="svgTextPath">
              <path
                :d="pathData.bezierArea"
                fill="none"
                stroke="none"
              />
            </clipPath>
          </g>
        </svg>
      </div>
      <svg
        v-if="svgVisible"
        :height="svgSizeData.height"
        :width="svgSizeData.width + svgBuffer"
        class="speed-static-line"
        xmlns="http://www.w3.org/2000/svg"
      >
        <!-- 曲线上的小球轨迹 -->
        <g>
          <circle
            fill="rgba(255,255,255,0.5)"
            r="10"
          />
          <circle
            fill="#fff"
            r="6"
          />
          <text
            :font-size="isMobile ? 10 : 14"
            :x="isMobile ? -26 : -29"
            :y="isMobile ? 22 : -15"
            fill="rgba(255,255,255,0.6)"
          >
            {{ currentSpeed }} MB/s
          </text>
          <animateMotion
            :dur="`${$props.dur}s`"
            :path="pathData.bezierPath"
            fill="freeze"
          />
        </g>
        <!-- 底部的直线 -->
        <g>
          <path
            :d="pathData.personalSpeedPath"
            fill="none"
            stroke="#3e3e46"
            style="stroke-width: 2px;"
          />
          <text
            :font-size="isMobile ? 10 : 14"
            :x="isMobile ? 330 : 703.5"
            :y="isMobile ? 97 : 198"
            fill="rgba(255,255,255,0.6)"
          >
            20 MB/s
          </text>
        </g>
      </svg>
    </div>

    <div class="speed-data">
      <div class="data-container person-data">
        <div>
          <span class="value">{{ speedAndFileData.average_speed }}</span><span class="other">MB/s</span>
        </div>
        <span class="other describe">{{ $t('home-speed-cloud-average') }}</span>
      </div>
      <div class="data-container amount-data">
        <div>
          <span class="value">{{ speedAndFileData.task_count }}</span>
        </div>
        <span class="other describe">{{ $t('home-speed-file-account') }}</span>
      </div>
      <div class="data-container global-data">
        <div>
          <span class="value">{{ speedAndFileData.total_speed }}</span><span class="other">GB/s</span>
        </div>
        <span class="other describe">{{ $t('home-speed-line-global') }}</span>
      </div>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import {
  reactive,
  ref,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  watch,
} from '#imports'
import globalSpeedLineSVG from '@/assets/images/home/global-speed-line.svg'
import personSpeedLineSVG from '@/assets/images/home/person-speed-line.svg'
import { useLayout } from '@/store/layout'
import {
  useIntersectionObserver,
  useMutationObserver,
} from '@vueuse/core'
import { type HomeSpeedAndFileData } from '~/api'

const enum PCSvgSize {
  width = 725,
  height = 211,
}

const enum MobileSvgSize {
  width = 351,
  height = 104,
}

const props = defineProps<{
  count: number;
  /** 动画时间 单位/秒 */
  dur: number;
  delay?: number;
  speedData: HomeSpeedAndFileData[];
}>()

interface EmitEvent {
  (e: 'update'): void
}

const emit = defineEmits<EmitEvent>()

const speedImageElementRef = ref<HTMLElement>()
const bezierPathRef = ref<SVGPathElement>()
const svgVisible = ref(false)

const pathData = reactive({
  bezierPath: '',
  bezierArea: '',
  personalSpeedPath: '',
})

const svgSizeData = reactive<{ width: number; height: number }>({
  width: PCSvgSize.width,
  height: PCSvgSize.height,
})

const isMobile = computed(() => useLayout().isMobile)
const svgBuffer = 50

const totalSpeedArr: { x: number, y: number }[] = []
const totalSpeedDisplayArr: number[] = []
const taskCountDisplayArr: number[] = []
const averageSpeedDisplayArr: number[] = []

const speedAndFileData = reactive({
  average_speed: 0,
  task_count: 0,
  total_speed: 0,
})

const currentSpeed = ref<number | string>(0)

const getGB = (byte: number) => Math.ceil(byte / 1024 / 1024 / 1024)
const getMB = (byte: number) => Math.ceil(byte / 1024 / 1024)
const getSvgX = (index: number) => Math.ceil((svgSizeData.width / 100) * index)
const getSvgY = (mb: number) => Math.ceil(svgSizeData.height - ((svgSizeData.height / 700) * mb))

const handleUpdateCurrentSpeed = () => {
  const durTime = props.dur * 1000
  const startTime = Date.now()
  const endTime = startTime + durTime
  const raf = () => {
    requestAnimationFrame(() => {
      const now = Date.now()
      if (now >= endTime) {
        return null
      }
      const index = Math.ceil(((now - startTime) / durTime) * props.count)
      if (totalSpeedArr[index]) {
        currentSpeed.value = totalSpeedArr[index].y
        speedAndFileData.total_speed = totalSpeedDisplayArr[index]
        speedAndFileData.task_count = taskCountDisplayArr[index]
        speedAndFileData.average_speed = averageSpeedDisplayArr[index]
      }
      raf()
    })
  }
  raf()
}

const handleUpdateBezierPath = () => {
  svgSizeData.width = isMobile.value ? MobileSvgSize.width : PCSvgSize.width
  svgSizeData.height = isMobile.value ? MobileSvgSize.height : PCSvgSize.height

  let Ipath = totalSpeedArr.length ? '' : 'M 0,0'
  let last: { x: number | string; y: number | string } = { x: 0, y: 0 }
  let useQ = false

  // 基于 Q/T 指令, 绘制平滑曲线
  totalSpeedArr.forEach((_, index) => {
    // 起点取第一个元素
    if (index === 0) {
      const y1 = getSvgY(_.y)
      Ipath += `M 0,${y1}`
      last = { x: 0, y: y1 }
    } else {
      const x2 = getSvgX(_.x)
      const y2 = getSvgY(_.y)

      if (!useQ) {
        Ipath += ` Q ${Math.ceil((+last.x + x2) / 2)} ${Math.ceil((+last.y + y2) / 2)},${x2} ${y2}`
        useQ = true
      } else {
        Ipath += ` T ${x2} ${y2}`
      }

      last = { x: x2, y: y2 }
    }
  })

  pathData.bezierPath = Ipath
  pathData.bezierArea = `${Ipath} L ${svgSizeData.width},${svgSizeData.height} L 0,${svgSizeData.height} Z`
  pathData.personalSpeedPath = `M 0,${getSvgY(20)} L ${svgSizeData.width},${getSvgY(20)}`
}

watch(bezierPathRef, () => {
  useMutationObserver(bezierPathRef.value, () => {
    // attribute d 发生变化时, 更新--speed-bezier-length
    // 获取贝塞尔曲线的长度
    const pathLength = bezierPathRef.value?.getTotalLength()
    if (pathLength) {
      let style = document.querySelector('#bezier-path')
      const styleInner = `:root{--speed-bezier-length: ${Math.ceil(pathLength)};--speed-bezier-dur: ${props.dur}s;}`

      if (!style) {
        style = document.createElement('style')
        style.setAttribute('id', 'bezier-path')
        document.head.appendChild(style)
      }

      style.innerHTML = styleInner
    }
  }, {
    attributes: true,
  })
})

let speedDataIndex = 0

const getNextSpeedData = async () => {
  emit('update')
  speedDataIndex = 0
}

// 闲时加载动画, 避免多段动画中的部分动画被阻塞, 造成动画不同步
const processAnimateIdle = (isIntersecting: boolean) => requestIdleCallback(() => {
  if (!isIntersecting) {
    svgVisible.value = isIntersecting
    return
  }

  const nextArr = props.speedData.slice(speedDataIndex, speedDataIndex + props.count)

  nextArr.forEach((item, index) => {
    const x = Math.ceil((index / (props.count - 1)) * 100)
    totalSpeedArr[index] = { x, y: getMB(item.average_speed) }
    totalSpeedDisplayArr[index] = getGB(item.total_speed)
    taskCountDisplayArr[index] = item.task_count
    averageSpeedDisplayArr[index] = getMB(item.average_speed)
  })

  handleUpdateBezierPath()
  handleUpdateCurrentSpeed()

  speedDataIndex += props.count

  if (speedDataIndex >= props.speedData.length) {
    getNextSpeedData()
  }

  if (nextArr.length === 0) {
    return
  }

  svgVisible.value = isIntersecting
})

let animateTimer: number | null = null

const destroyAnimateTimer = () => {
  if (animateTimer) {
    clearInterval(animateTimer)
    animateTimer = null
  }
}

const startAnimate = () => {
  destroyAnimateTimer()

  const fn = () => {
    if (svgVisible.value) {
      svgVisible.value = false
    }
    nextTick(() => {
      processAnimateIdle(true)
    })
    return fn
  }
  animateTimer = window.setInterval(fn(), props.dur * 1000 + (props?.delay || 0) * 1000)
}

watch(isMobile, () => {
  handleUpdateBezierPath()
  startAnimate()
})

onMounted(handleUpdateBezierPath)

onUnmounted(() => {
  destroyAnimateTimer()
})

const { stop } = useIntersectionObserver(speedImageElementRef, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    startAnimate()
    stop()
  }
})

</script>

<style
  lang="less"
  scoped
>
@import '@/assets/styles/util.less';

.speed {
  &-chart-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &-line-description {
    display: flex;
    margin-top: 114px;

    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: #fff;
    opacity: 0.6;

    .box {
      display: flex;

      &.global {
        margin-right: 48px;
      }

      img {
        margin-right: 12px;
      }
    }
  }

  &-canvas-container {
    margin-top: 32px;
    width: 763px;
    height: 211px;
    background: url('@/assets/images/home/speed-mesh.svg') no-repeat;
    background-size: 730px auto;
    overflow: hidden;
    position: relative;

    .actual-size {
      width: 100%;
      height: 100%;
      transform: translate(4px, -4px);
      overflow: hidden;
      position: relative;
      clip-path: url(#svgTextPath);
    }

    .actual-size-animate {
      &::after {
        content: '';
        width: calc(var(--speed-bezier-length) * 1px);
        height: 100%;
        position: absolute;
        left: 0;
        top: 34px;
        background: url('@/assets/images/home/speed-mesh-blue.svg') no-repeat;
        background-size: 100% auto;
        transform: translateX(-100%);
        animation: dash var(--speed-bezier-dur) linear 1 both;
        z-index: -1;
      }
    }
  }

  &-svg {
    /* 取贝塞尔曲线的长度 */
    stroke-dasharray: var(--speed-bezier-length);
    stroke-dashoffset: var(--speed-bezier-length);

    &-line {
      transform: translateY(1px);
    }
  }

  &-svg-animate {
    animation: dash var(--speed-bezier-dur) linear 1 both;
  }

  &-circle {
    transform: translate(4px, -215px);
  }

  &-static-line {
    position: absolute;
    left: 4px;
    top: -3px;
  }

  &-data {
    width: 856px;
    height: 78px;
    margin-top: 86px;
    display: flex;
    justify-content: space-between;

    .data-container {
      width: 200px;
      height: 78px;
      color: #fff;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      line-height: 1.5em;

      .value {
        font-weight: 700;
        font-size: 64px;
      }

      .other {
        margin-left: 5px;
        font-weight: 700;
        font-size: 14px;
        opacity: 0.6;

        &.describe {
          font-weight: 400;
          margin-top: 8px;
        }
      }
    }
  }
}

@keyframes dash {
  to {
    stroke-dashoffset: 0;
    transform: translateX(0);
  }
}

@media screen and (max-width: @mobile-break-width) {
  .speed {
    &-line-description {
      margin-top: 54px;
      flex-direction: column;

      .person {
        margin-top: 8px;
      }
    }

    &-canvas-container {
      width: 375px;
      height: 138px;

      background-size: 354px auto;

      .actual-size {
        transform: translate(2px, -5px);

        &::after {
          top: 15px;
        }
      }
    }

    &-circle {
      transform: translate(2px, -143px);
    }

    &-static-line {
      left: 2px;
      top: -4px;
    }

    &-data {
      width: 100%;
      margin-top: 50px;
      padding: 0 20px;

      .data-container {
        font-size: 12px;
        line-height: 18px;
        text-align: center;
        margin-right: 10px;

        .value {
          font-size: 30px;
          line-height: 30px;
        }

        .other {
          font-size: 12px;
          line-height: 18px;
          margin-left: 4px;

          &.describe {
            margin-top: 8px;
          }
        }
      }
    }
  }
}
</style>
